import React, {useCallback, useEffect, useState} from 'react';
import {useParams, useSearchParams} from 'react-router-dom';
import {CircularProgress} from '@mui/material';
import {enqueueSnackbar} from 'notistack';
import {logError} from '../../shared/services/logger.service';
import {getCustomerFacingCompany} from '../../shared/services/company.service';
import {ExternalCompanyDto} from '../../shared/dtos/company.dto';
import {EstimateStatus} from '../../shared/dtos/estimate.dto';
import {CustomerFacingEstimateDto} from '../../shared/dtos/customer-facing-estimate-data.dto';
import {CompanyBranding, RecordMessage, RecordPage, TitleRow} from '../../shared/components/record/RecordKit';
import {ClientCard} from '../client/profile/ClientCard';
import {ClientFields} from '../client/profile/contact-entries';
import {customerApproveEstimate, getCustomerFacingEstimate} from '../estimate/estimate.service';
import {getCustomerFacingInvoice} from '../invoice/invoice.service';
import {CustomerFacingInvoiceDto} from '../invoice/dtos/customer-facing-invoice.dto';
import {getCustomerFacingReceipt} from '../receipt/receipt.service';
import WorkItemCard, {RecordContext} from '../estimate/record/WorkItemCard';
import {recordStatusLabel} from '../estimate/record/EstimateRecordPage';
import {customerInvoiceTotals, invoiceStatusLabel} from '../estimate/record/invoice-math';
import {ConfirmBlock, CustomerEstimateTotal, CustomerInvoiceTotals, PayBlock} from '../estimate/record/CustomerPieces';
import {photosOf} from '../estimate/record/PhotoPager';
import {PhotoViewer} from '../../shared/components/PhotoViewer';
import '../client/profile/ClientProfile.css';
import '../appointment/record/AppointmentPage.css';
import '../estimate/record/EstimateRecord.css';
import './CustomerRecord.css';

/*
 * The customer's link to an estimate, an invoice or a receipt (SQ-337):
 * /external/estimates/:id, /external/invoices/:id and /external/receipts/:id,
 * reached from a short link (ShortLinkRedirectPage), the accessToken in the
 * query being the customer's only credential. The record's own page in the
 * customer's form, the same as the staff page's Preview: the status strip
 * (no Edit, no button menu), the title and number, the company, the
 * client's primary lines, every work item (photos, site line, measurements,
 * operations with their notes, the Work Plan's first section), then the
 * estimate's total and Confirm while it waits for them, or the invoice's
 * total, what was paid and the balance, and Pay while anything is owed. No
 * app bar: it is outside the signed-in layout.
 */

export type CustomerRecordKind = 'estimate' | 'invoice' | 'receipt';

const CUSTOMER_CONTEXT: RecordContext = {
  crewSize: 0, crewSelected: false, itemMinutes: new Map(), operationMinutes: new Map(), aiRequested: false, aiPricing: false, customer: true, pricesHidden: true,
};

const NO_OP = () => undefined;

/** The company's logo: the one it uploaded, else where logos were kept before (the old link's address). */
export const companyLogoUrl = (company: ExternalCompanyDto) => {
  if (company.logoUrl) return company.logoUrl;
  const base = process.env.REACT_APP_PUBLIC_ASSETS_BASE_URL;
  return base ? `${base}/companies/${company.id}/assets/logo.png` : undefined;
};

type CustomerRecord = CustomerFacingEstimateDto | CustomerFacingInvoiceDto;

const isInvoice = (record: CustomerRecord): record is CustomerFacingInvoiceDto => 'companyInvoiceId' in record;

/** The client's primary lines: name, phone, the mailing address (street, then "City, ST 12345") and email. */
export const customerClientFields = (record: CustomerRecord): ClientFields => ({
  name: record.clientName ?? '', phoneNumber: record.clientPhoneNumber ?? '', email: record.clientEmail ?? '',
  address: (record.clientAddress ?? '').trim(), city: record.clientCity?.trim() ?? '', state: record.clientState?.trim() ?? '',
  postalCode: record.clientPostalCode?.trim() ?? '',
  lat: 0, lng: 0,
});

const CustomerRecordPage = ({kind}: {kind: CustomerRecordKind}) => {
  const params = useParams();
  const id = Number(kind === 'estimate' ? params.estimateId : params.invoiceId);
  const [searchParams] = useSearchParams();
  const accessToken = searchParams.get('accessToken');

  const [record, setRecord] = useState<CustomerRecord>();
  const [company, setCompany] = useState<ExternalCompanyDto>();
  const [loadError, setLoadError] = useState<string>();
  // A deleted estimate answers 404: it is gone, the way a deleted invoice is.
  const [gone, setGone] = useState(false);
  const [working, setWorking] = useState(false);
  // A work item's photos in the shared viewer (SQ-657), from the one tapped: to look at, never to change.
  const [viewing, setViewing] = useState<{treeId: number; key: string}>();

  const load = useCallback(async () => {
    if (!accessToken) throw new Error('This link is missing its access code.');
    return kind === 'estimate' ? getCustomerFacingEstimate(accessToken, id)
      : kind === 'invoice' ? getCustomerFacingInvoice(accessToken, id) : getCustomerFacingReceipt(accessToken, id);
  }, [accessToken, kind, id]);

  useEffect(() => {
    let active = true;
    load()
      .then(loaded => {
        if (!active) return;
        setRecord(loaded);
        getCustomerFacingCompany(loaded.companyId).then(c => active && setCompany(c)).catch(() => undefined);
      })
      .catch(e => {
        if (!active) return;
        if (kind === 'estimate' && (e as {status?: number}).status === 404) {
          setGone(true);
          return;
        }
        logError('Error loading the customer link', {kind, id}, e as Error);
        setLoadError(e instanceof Error && e.message ? e.message : `Couldn't load this ${kind}.`);
      });
    return () => { active = false; };
  }, [load, kind, id]);

  /**
   * Sign and Confirm: the drawn signature approves the estimate; the page reads it again (Approved, no Confirm).
   * Once the approval is in, a failed re-read still shows it approved: asking again would only be refused.
   */
  const sign = async (file: File) => {
    if (!accessToken || working) return;
    setWorking(true);
    try {
      try {
        await customerApproveEstimate(accessToken, id, file);
      } catch (e) {
        logError('Error confirming the estimate', {id}, e as Error);
        enqueueSnackbar("Couldn't confirm the estimate. Please try again.", {variant: 'error', autoHideDuration: 5000});
        return;
      }
      enqueueSnackbar('Estimate confirmed. Thank you!', {variant: 'success', autoHideDuration: 5000});
      try {
        setRecord(await load());
      } catch (e) {
        logError('Error reloading the confirmed estimate', {id}, e as Error);
        setRecord(r => r && !isInvoice(r) ? {...r, status: EstimateStatus.EstimateApproved} : r);
      }
    } finally {
      setWorking(false);
    }
  };

  const invoice = record && isInvoice(record) ? record : undefined;
  const estimate = record && !isInvoice(record) ? record : undefined;
  const deleted = invoice?.status === 'Deleted';
  const status = invoice ? invoiceStatusLabel(invoice.status) : estimate ? recordStatusLabel(estimate.status) : '';
  const title = invoice ? `${kind === 'receipt' ? 'Receipt' : 'Invoice'} #${invoice.companyInvoiceId}` : `Estimate #${id}`;
  const payable = !!invoice && customerInvoiceTotals(invoice.totalCost, invoice.amountPaid ?? 0, invoice.status).payable;

  const viewerTree = viewing && record?.trees.find(t => t.id === viewing.treeId);
  const viewerPhotos = viewerTree && photosOf(viewerTree, record?.gates ?? [])
    .map(p => ({id: p.key, url: p.url, badge: p.gate !== undefined ? `Gate ${p.gate}` : undefined}));

  return (
    <div className="cv-frame">
      <RecordPage name="CustomerRecordPage" className="er-page" status={status}>
        {loadError && <RecordMessage error text={loadError}/>}
        {!record && !loadError && !gone && <CircularProgress/>}
        {gone && <RecordMessage text="This Estimate is No Longer Available"/>}
        {deleted && <RecordMessage text="This Invoice is No Longer Available"/>}
        {record && !deleted && <>
          <TitleRow title={title}/>
          {company && <CompanyBranding name={company.name} logoUrl={companyLogoUrl(company)}/>}
          <div className="ap-client-heading">
            <span className="ap-client-label">CLIENT</span>
          </div>
          <ClientCard question={{client: customerClientFields(record), entries: [], prompt: null, picks: {}}} onPick={NO_OP}
                      onStar={NO_OP} onSaveReview={async () => false} additionalOpen={false} onToggleAdditional={NO_OP} customer/>

          <div className="er-section">
            <div className="er-items">
              {record.trees.length === 0 && <div className="er-muted">No work items.</div>}
              {record.trees.map((tree, i) => (
                <WorkItemCard key={tree.id} tree={tree} number={i + 1} gates={record.gates ?? []} ctx={CUSTOMER_CONTEXT}
                              onOpenPhoto={p => setViewing({treeId: tree.id, key: p.key})}/>
              ))}
            </div>
            <div className="er-footer-rule"/>
            {invoice
              ? <CustomerInvoiceTotals total={invoice.totalCost} paid={invoice.amountPaid ?? 0} status={invoice.status}/>
              : <CustomerEstimateTotal total={record.totalCost}/>}
            {/* The server takes a signature only while the estimate is Pending Approval. */}
            {estimate?.status === EstimateStatus.EstimatePendingApproval && <ConfirmBlock working={working} onSign={file => void sign(file)}/>}
            {payable && <PayBlock companyId={record.companyId}/>}
          </div>
        </>}

        {viewerPhotos && (
          <PhotoViewer photos={viewerPhotos} startIndex={Math.max(0, viewerPhotos.findIndex(p => p.id === viewing?.key))}
                       onClose={() => setViewing(undefined)}/>
        )}
      </RecordPage>
    </div>
  );
};

export default CustomerRecordPage;
